
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useProjects } from '@/hooks/useProjects';
import { useTimeStore } from '@/store/timeStore';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import TimeDisplay from './TimeDisplay';
import ProjectSelector from './ProjectSelector';
import SessionInfo from './SessionInfo';
import UserStatus from './UserStatus';
import { getCurrentUser as getCurrentUserFromAuth, type CurrentUser } from '@/utils/authUtils';
import { EquipmentDialog } from '@/components/Equipment/EquipmentDialog';

// Helper pour détecter le mode PIN
const shouldUsePinMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  const authMethod = window.localStorage.getItem('auth_method');
  const managerPin = window.localStorage.getItem('manager_pin');
  return authMethod === 'pin' && Boolean(managerPin);
};

// Helper pour obtenir le PIN utilisateur
const getUserPinOrThrow = (): string => {
  const pin = typeof window !== 'undefined' ? window.localStorage.getItem('manager_pin') : null;
  if (!pin) throw new Error('PIN utilisateur manquant');
  return pin;
};

const ActiveTimeTracker: React.FC = React.memo((
) => {
  const [showSwitch, setShowSwitch] = useState(false);
  const [switchProjectId, setSwitchProjectId] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [description, setDescription] = useState('');
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showLunchOptions, setShowLunchOptions] = useState(false);
  const [showEquipmentDialog, setShowEquipmentDialog] = useState(false);
  const [closedEntryId, setClosedEntryId] = useState<string | null>(null);
  const [closedProjectName, setClosedProjectName] = useState('');
  const [closedHoursWorked, setClosedHoursWorked] = useState('');

  const { projects, loading: projectsLoading } = useProjects();
  const {
    activeSession,
    isLoading,
    error,
    startTracking,
    stopTracking,
    setActiveSession,
  } = useTimeStore();
  const { toast } = useToast();

  // Récupérer l'utilisateur connecté
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const user = await getCurrentUserFromAuth();
        setCurrentUser(user);
        console.log('✅ Utilisateur connecté:', user?.email);
      } catch (error) {
        console.error('❌ Erreur auth:', error);
      }
    };
    getCurrentUser();
  }, []);

  // Vérifier s'il y a une session active au chargement
  useEffect(() => {
    const checkActiveSession = async () => {
      if (!currentUser) return;

      try {
        console.log('🔍 Vérification session active pour:', currentUser.email);
        
        let activeEntry: any = null;
        
        if (shouldUsePinMode()) {
          // Mode PIN : utiliser la RPC PIN mode pour lister les entrées actives
          const pin = getUserPinOrThrow();
          const now = new Date();
          const pastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          
          const { data, error } = await supabase.rpc('pin_list_user_time_entries', {
            p_pin: pin,
            p_user_id: currentUser.id,
            p_start: pastWeek.toISOString(),
            p_end: now.toISOString(),
            p_limit: 100,
            p_offset: 0
          });
          
          if (error && error.code !== 'PGRST116') throw error;
          
          // Trouver la première entrée sans end_time
          activeEntry = (data || []).find((entry: any) => !entry.end_time) || null;
        } else {
          // Mode JWT : utiliser l'API Supabase standard
          const { data, error } = await supabase
            .from('time_entries')
            .select('*')
            .eq('user_id', currentUser.id)
            .is('end_time', null)
            .order('start_time', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (error && error.code !== 'PGRST116') throw error;
          activeEntry = data;
        }

        if (activeEntry) {
          console.log('✅ Session active trouvée:', activeEntry);
          setActiveSession({
            id: activeEntry.id,
            projectId: activeEntry.project_id,
            startTime: new Date(activeEntry.start_time),
            description: activeEntry.description || ''
          });
        } else {
          console.log('ℹ️ Aucune session active');
          setActiveSession(null);
        }
      } catch (error) {
        console.error('❌ Erreur session active:', error);
      }
    };

    checkActiveSession();
  }, [currentUser]);

  // Calculer le temps écoulé pour la session active
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (activeSession) {
      interval = setInterval(() => {
        const now = Date.now();
        const sessionStart = typeof activeSession.startTime === 'string' ? new Date(activeSession.startTime) : activeSession.startTime;
        const elapsed = now - sessionStart.getTime();
        setElapsedTime(elapsed);
      }, 1000);
    } else {
      setElapsedTime(0);
    }
    return () => clearInterval(interval);
  }, [activeSession]);

  // Gestionnaire de démarrage optimisé
  const handleStart = useCallback(async () => {
    if (!currentUser) {
      toast({
        title: "❌ Erreur d'authentification",
        description: "Vous devez être connecté pour commencer à travailler",
        variant: "destructive",
      });
      return;
    }

    if (!selectedProject) {
      toast({
        title: "❌ Projet requis",
        description: "Veuillez sélectionner un projet avant de commencer",
        variant: "destructive",
      });
      return;
    }

    try {
      await startTracking(selectedProject, description);
      toast({
        title: "✅ Shift démarré",
        description: `Suivi du temps commencé pour ${projects.find(p => p.id === selectedProject)?.name || 'le projet'}`,
      });
    } catch (error) {
      console.error('❌ Erreur démarrage:', error);
      toast({
        title: "❌ Erreur",
        description: "Impossible de démarrer le shift",
        variant: "destructive",
      });
    }
  }, [currentUser, selectedProject, description, projects, startTracking, toast]);

  // Gestionnaire d'arrêt optimisé
  const handleStop = useCallback(async () => {
    if (!activeSession) {
      toast({
        title: "❌ Erreur",
        description: "Aucune session active à arrêter",
        variant: "destructive",
      });
      return;
    }

    try {
      const entryId = activeSession.id;
      const projectId = activeSession.projectId;
      const projectName = projects.find(p => p.id === projectId)?.name || 'Projet';
      const hoursWorked = ((Date.now() - (typeof activeSession.startTime === 'string' ? new Date(activeSession.startTime).getTime() : activeSession.startTime.getTime())) / (1000 * 60 * 60)).toFixed(2);

      await stopTracking();

      setClosedEntryId(entryId);
      setClosedProjectName(projectName);
      setClosedHoursWorked(`${hoursWorked}h`);
      setShowEquipmentDialog(true);
    } catch (error) {
      console.error('❌ Erreur arrêt shift:', error);
      toast({
        title: "❌ Erreur",
        description: "Impossible d'enregistrer le temps de travail",
        variant: "destructive",
      });
    }
  }, [activeSession, stopTracking, toast, projects]);

  const handleEquipmentDialogClose = useCallback((hasEquipment: boolean) => {
    setShowEquipmentDialog(false);
    toast({
      title: "✅ Shift terminé",
      description: hasEquipment
        ? `${closedHoursWorked} enregistrées. Équipements signalés.`
        : `${closedHoursWorked} enregistrées.`,
    });
    setClosedEntryId(null);
  }, [toast, closedHoursWorked]);

  // Gestionnaire de changement de projet
  const handleConfirmSwitch = useCallback(async () => {
    if (!switchProjectId) return;
    try {
      await stopTracking();
      setDescription(''); // Réinitialiser la description pour le nouveau shift
      await startTracking(switchProjectId, '');
      setShowSwitch(false);
      setSwitchProjectId('');
      toast({ title: '✅ Projet changé', description: 'Le suivi continue sur le nouveau projet.' });
    } catch (error) {
      toast({ title: '❌ Erreur', description: 'Impossible de changer de projet', variant: 'destructive' });
    }
  }, [switchProjectId, stopTracking, startTracking, toast]);

  // Gestionnaire de pause dîner avec durée sélectionnée
  const handleLunch = useCallback(async (durationMinutes: number) => {
    if (!currentUser || !activeSession) {
      toast({ title: '❌ Pas de shift actif', variant: 'destructive' });
      return;
    }

    try {
      const startLunch = new Date();
      const endLunch = new Date(startLunch.getTime() + durationMinutes * 60 * 1000);
      const projectId = activeSession.projectId;

      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { data: entryId, error: pauseError } = await supabase.rpc('pin_create_time_entry', {
          p_pin: pin,
          p_user_id: currentUser.id,
          p_project_id: projectId,
          p_start_time: startLunch.toISOString(),
          p_description: `Pause dîner (${durationMinutes} min)`,
          p_is_billable: false,
          p_status: 'closed'
        });

        if (pauseError) throw pauseError;
        if (!entryId) throw new Error('Aucun ID retourné pour la pause');

        const { error: updateError } = await supabase.rpc('pin_update_time_entry', {
          p_pin: pin,
          p_entry_id: entryId,
          p_end_time: endLunch.toISOString(),
          p_duration_minutes: durationMinutes,
          p_description: null,
          p_is_billable: null,
          p_status: null,
          p_project_id: null
        });

        if (updateError) throw updateError;
      } else {
        const { error: pauseError } = await supabase
          .from('time_entries')
          .insert({
            user_id: currentUser.id,
            project_id: projectId,
            start_time: startLunch.toISOString(),
            end_time: endLunch.toISOString(),
            duration_minutes: durationMinutes,
            description: `Pause dîner (${durationMinutes} min)`,
            is_billable: false,
            status: 'closed',
          });

        if (pauseError) throw pauseError;
      }

      setShowLunchOptions(false);
      toast({ title: '🍽️ Pause dîner enregistrée', description: `${durationMinutes} minutes ajoutées` });

    } catch (err) {
      console.error(err);
      toast({ title: '❌ Erreur', description: "Impossible d'enregistrer la pause.", variant: 'destructive' });
    }
  }, [currentUser, activeSession, toast]);

  // Gestionnaire de mise à jour de la description pendant un shift actif
  const handleDescriptionUpdate = useCallback(async (newDescription: string) => {
    if (!activeSession) return;

    try {
      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();
        const { error } = await supabase.rpc('pin_update_time_entry', {
          p_pin: pin,
          p_entry_id: activeSession.id,
          p_end_time: null,
          p_duration_minutes: null,
          p_description: newDescription,
          p_is_billable: null,
          p_status: null,
          p_project_id: null,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('time_entries')
          .update({ description: newDescription })
          .eq('id', activeSession.id);
        if (error) throw error;
      }

      // Mettre à jour le state local
      setActiveSession({
        ...activeSession,
        description: newDescription,
      });
      setDescription(newDescription);

      toast({
        title: '✅ Description mise à jour',
        description: 'La description du shift a été enregistrée',
      });
    } catch (error) {
      console.error('❌ Erreur mise à jour description:', error);
      toast({
        title: '❌ Erreur',
        description: 'Impossible de mettre à jour la description',
        variant: 'destructive',
      });
      throw error;
    }
  }, [activeSession, setActiveSession, toast]);

  // Validation des conditions pour démarrer
  const canStart = useMemo(() => {
    return currentUser && selectedProject && !activeSession;
  }, [currentUser, selectedProject, activeSession]);

  // Interface de chargement
  if (projectsLoading) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="p-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <div>Chargement des projets...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Affichage des erreurs
  if (error) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="p-6">
          <div className="text-center text-red-600">
            <div className="mb-2">⚠️ Erreur</div>
            <div className="text-sm">{error}</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-center">⏱️ Suivi du Temps</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ProjectSelector
          projects={projects}
          selectedProject={activeSession ? activeSession.projectId : selectedProject}
          description={activeSession ? (activeSession.description || '') : description}
          onProjectChange={setSelectedProject}
          onDescriptionChange={setDescription}
          disabled={!!activeSession}
          isTracking={!!activeSession}
          onDescriptionUpdate={handleDescriptionUpdate}
        />

        {activeSession && (
          <>
            <Button
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setShowSwitch(!showSwitch)}
            >
              Changer de projet
            </Button>
            {showSwitch && (
              <div className="space-y-2">
                <select
                  className="w-full border rounded p-2"
                  value={switchProjectId}
                  onChange={(e) => setSwitchProjectId(e.target.value)}
                >
                  <option value="">Sélectionner un projet</option>
                  {projects
                    .filter(p => p.is_active && (!activeSession || p.id !== activeSession.projectId))
                    .map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
                <div className="flex gap-2">
                  <Button className="flex-1" onClick={handleConfirmSwitch} disabled={!switchProjectId}>
                    Valider
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => { setShowSwitch(false); setSwitchProjectId(''); }}>
                    Annuler
                  </Button>
                </div>
              </div>
            )}
            <Button
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-white"
              onClick={() => setShowLunchOptions(!showLunchOptions)}
            >
              🍽️ Ajouter pause dîner
            </Button>
            {showLunchOptions && (
              <div className="grid grid-cols-4 gap-2">
                {[15, 30, 45, 60].map((minutes) => (
                  <Button
                    key={minutes}
                    variant="outline"
                    size="sm"
                    onClick={() => handleLunch(minutes)}
                    className="text-xs"
                  >
                    {minutes} min
                  </Button>
                ))}
              </div>
            )}
          </>
        )}

        <TimeDisplay
          elapsedTime={elapsedTime}
          isTracking={!!activeSession}
          onStart={handleStart}
          onStop={handleStop}
          canStart={canStart}
          isLoading={isLoading}
        />


        <SessionInfo
          isTracking={!!activeSession}
          currentEntryId={activeSession?.id ?? null}
          selectedProject={activeSession ? activeSession.projectId : selectedProject}
          projects={projects}
          startTime={activeSession?.startTime ?? null}
        />

        <UserStatus currentUser={currentUser} />
      </CardContent>

      {showEquipmentDialog && closedEntryId && (
        <EquipmentDialog
          open={showEquipmentDialog}
          onClose={handleEquipmentDialogClose}
          timeEntryId={closedEntryId}
          projectName={closedProjectName}
          hoursWorked={closedHoursWorked}
        />
      )}
    </Card>
  );
});

ActiveTimeTracker.displayName = 'ActiveTimeTracker';

export default ActiveTimeTracker;
